import React from "react";
import Linkify from "react-linkify";
import { useUserContext } from "store/contexts/userContext";
import { useTeamContext } from "store/contexts/teamContext";
import { useTeamActionContext } from "store/contexts/teamActionContext";
import { SURVEY_STATUS } from "store/reducers/surveyReducer";

export default ({ post }) => {
  const { user } = useUserContext();
  const { team } = useTeamContext();
  const { teamId } = useTeamActionContext();
  
  // Check if the survey was created in the current team
  const isCreatedInCurrentTeam = post.team_id === parseInt(teamId);
  
  // Check if the current user created the survey
  const isCreatedByCurrentUser = post.user && user && post.user.id === user.id;
  
  // Check if the current user is an admin in the team
  const isAdmin = team && [2, 3].includes(team.role_id);
  
  // Determine if the user can manage the survey - only if they're the creator or admin AND the survey was created in this team
  const canManageSurvey = (isCreatedByCurrentUser || isAdmin) && isCreatedInCurrentTeam;
  
  // Get survey status text
  const getStatusText = (status) => {
    if (status === SURVEY_STATUS.ACTIVE) {
      return "Published";
    }
    if (status === SURVEY_STATUS.DRAFT) {
      return "Draft";
    }
    return "Closed";
  };
  
  const surveyStatus = post.status !== undefined ? post.status : SURVEY_STATUS.INACTIVE;
  
  return (
    <div className="survey-post-content">
      <h4 className="survey-title">{post.title || post.data?.content}</h4>
      <Linkify
        properties={{
          target: "_blank",
          style: { color: "red", fontWeight: "bold" },
        }}
      >
        <p className="survey-description textContent">{post.description}</p>
      </Linkify>
      
      {/* Survey creation info */}
      <div className="survey-creation-info" style={{ fontSize: '0.85rem', marginTop: '10px', color: '#666' }}>
        {isCreatedByCurrentUser ? (
          <span>You created this survey {!isCreatedInCurrentTeam && ` in ${post.team?.name}`}</span>
        ) : (
          <span>Created by {post.user?.name} {!isCreatedInCurrentTeam && ` in ${post.team?.name}`}</span>
        )}
        
        {/* Survey status indicator */}
        <div className="survey-status-indicator" style={{ marginTop: '5px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
          <span 
            className="status-badge" 
            style={{ 
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              backgroundColor: isCreatedInCurrentTeam ? '#e6f7ff' : '#fff7e6',
              color: isCreatedInCurrentTeam ? '#1890ff' : '#fa8c16',
              border: isCreatedInCurrentTeam ? '1px solid #91d5ff' : '1px solid #ffd591',
              marginRight: '5px'
            }}
          >
            {isCreatedInCurrentTeam ? 'Created in this team' : 'Assigned to this team'}
          </span>
          
          <span 
            className="status-badge" 
            style={{ 
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              backgroundColor: surveyStatus === SURVEY_STATUS.ACTIVE ? '#f6ffed' : 
                             surveyStatus === SURVEY_STATUS.DRAFT ? '#fff7e6' : '#fff1f0',
              color: surveyStatus === SURVEY_STATUS.ACTIVE ? '#52c41a' : 
                     surveyStatus === SURVEY_STATUS.DRAFT ? '#fa8c16' : '#ff4d4f',
              border: surveyStatus === SURVEY_STATUS.ACTIVE ? '1px solid #b7eb8f' : 
                      surveyStatus === SURVEY_STATUS.DRAFT ? '1px solid #ffd591' : '1px solid #ffccc7',
            }}
          >
            {getStatusText(surveyStatus)}
          </span>
        </div>
        
        {/* Survey access indicator */}
        <div className="survey-access-indicator" style={{ marginTop: '5px', display: 'flex', alignItems: 'center' }}>
          <span 
            className="access-badge" 
            style={{ 
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              backgroundColor: canManageSurvey ? '#e6f7ff' : '#f6ffed',
              color: canManageSurvey ? '#1890ff' : '#52c41a',
              border: canManageSurvey ? '1px solid #91d5ff' : '1px solid #b7eb8f',
              marginRight: '5px'
            }}
          >
            {canManageSurvey ? 'Manage' : 'Take Survey'}
          </span>
          <span>
            {canManageSurvey 
              ? 'You can manage this survey' 
              : 'You can take this survey'}
          </span>
        </div>
      </div>
    </div>
  );
};

